import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Pencil, Archive, Trash2, Loader2, Copy, Users, Lock, LockOpen } from "lucide-react";
import adminApi, { apiError } from "@/lib/adminApi";
import { StatusBadge } from "@/pages/admin/AdminLayout";
import { formatDateRange, formatPrice } from "@/lib/format";

const STATUS_OPTIONS = [
  { value: "", label: "Tous les statuts" },
  { value: "open", label: "Ouvert" },
  { value: "full", label: "Complet" },
  { value: "closed", label: "Fermé" },
  { value: "draft", label: "Brouillon" },
  { value: "archived", label: "Archivé" },
];

const SORT_OPTIONS = [
  { value: "date_asc", label: "Date la plus proche" },
  { value: "date_desc", label: "Date la plus éloignée" },
  { value: "inscrits", label: "Nombre d'inscrits" },
  { value: "places", label: "Places restantes" },
  { value: "prix", label: "Prix" },
];

const PERIOD_OPTIONS = [
  { value: "", label: "Tous" },
  { value: "upcoming", label: "À venir" },
  { value: "past", label: "Passés" },
];

const AdminStages = ({ type, title }) => {
  const [searchParams] = useSearchParams();
  const [stages, setStages] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [period, setPeriod] = useState("");
  const [sort, setSort] = useState("date_asc");
  const [confirm, setConfirm] = useState(null); // {kind: 'delete'|'close'|'open'|'archive', stage}
  const [busy, setBusy] = useState(false);

  const load = () => adminApi.get("/stages", { params: { type } }).then(({ data }) => setStages(data)).catch(() => {});
  useEffect(() => { load(); }, [type]);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = stages
    .filter((s) => (!statusFilter || (s.effective_status === statusFilter || s.status === statusFilter)))
    .filter((s) => !period || (period === "upcoming" ? s.end_date >= today : s.end_date < today))
    .filter((s) => !q || s.name.toLowerCase().includes(q.toLowerCase()) || s.location.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => {
      if (sort === "date_asc") return a.start_date.localeCompare(b.start_date);
      if (sort === "date_desc") return b.start_date.localeCompare(a.start_date);
      if (sort === "inscrits") return b.confirmed_count - a.confirmed_count;
      if (sort === "places") return b.places_restantes - a.places_restantes;
      if (sort === "prix") return b.price - a.price;
      return 0;
    });

  const runAction = async () => {
    if (!confirm) return;
    setBusy(true);
    const { kind, stage } = confirm;
    try {
      if (kind === "delete") {
        await adminApi.delete(`/stages/${stage.id}`);
        toast.success("Stage supprimé.");
      } else if (kind === "archive") {
        await adminApi.post(`/stages/${stage.id}/archive`);
        toast.success("Stage archivé.");
      } else if (kind === "close" || kind === "open") {
        await adminApi.put(`/stages/${stage.id}`, {
          name: stage.name, description: stage.description, image: stage.image,
          start_date: stage.start_date, end_date: stage.end_date,
          location: stage.location, location_details: stage.location_details,
          price: stage.price, capacity: stage.capacity, categories: stage.categories,
          stage_type: stage.stage_type,
          status: kind === "close" ? "closed" : "open",
        });
        toast.success(kind === "close" ? "Inscriptions fermées." : "Inscriptions rouvertes.");
      }
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(apiError(e));
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const duplicate = async (stage) => {
    setBusy(true);
    try {
      await adminApi.post(`/stages/${stage.id}/duplicate`);
      toast.success("Stage dupliqué en brouillon — modifiez-le avant publication.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const CONFIRM_TEXT = {
    delete: { title: "Supprimer ce stage ?", text: "Si le stage possède des inscriptions ou des paiements, la suppression sera refusée — utilisez l'archivage.", label: "Supprimer" },
    archive: { title: "Archiver ce stage ?", text: "Le stage disparaîtra des prochains stages publics mais conservera tout son historique.", label: "Archiver" },
    close: { title: "Fermer les inscriptions ?", text: "Les visiteurs ne pourront plus s'inscrire à ce stage.", label: "Confirmer" },
    open: { title: "Rouvrir les inscriptions ?", text: "Le stage redeviendra ouvert aux inscriptions publiques.", label: "Confirmer" },
  };

  return (
    <div data-testid={`admin-stages-${type}`} className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="font-display uppercase text-4xl tracking-wide">{title}</h1>
        <Link
          to={`/admin/stages/nouveau?type=${type}`}
          data-testid="admin-add-stage"
          className="inline-flex items-center gap-2 px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover transition-colors"
        >
          <Plus size={15} /> Ajouter un stage
        </Link>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
          <input
            data-testid="stages-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher un stage…"
            className="w-full bg-noir-surface border border-noir-border text-white pl-11 pr-4 py-3 text-sm focus:border-gold outline-none"
          />
        </div>
        <select data-testid="stages-status-filter" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-noir-surface border border-noir-border text-white px-4 py-3 text-sm focus:border-gold outline-none">
          {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select data-testid="stages-period-filter" value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-noir-surface border border-noir-border text-white px-4 py-3 text-sm focus:border-gold outline-none">
          {PERIOD_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select data-testid="stages-sort" value={sort} onChange={(e) => setSort(e.target.value)} className="bg-noir-surface border border-noir-border text-white px-4 py-3 text-sm focus:border-gold outline-none">
          {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>

      <div className="space-y-3">
        {filtered.map((s) => (
          <div key={s.id} data-testid={`admin-stage-row-${s.slug}`} className="bg-noir-surface border border-noir-border p-5">
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3">
              <div className="min-w-0 flex-1">
                <p className="font-display text-xl tracking-wide uppercase truncate">{s.name}</p>
                <p className="text-xs text-white/50 mt-1">{formatDateRange(s.start_date, s.end_date)} — {s.location}</p>
              </div>
              <p className="font-display text-2xl text-gold">{formatPrice(s.price)}</p>
              <p className="text-sm text-white/60"><span className="text-gold font-bold">{s.confirmed_count}/{s.capacity}</span> inscrits</p>
              <StatusBadge status={s.effective_status} />
            </div>
            <div className="mt-4 pt-4 border-t border-white/5 flex flex-wrap items-center gap-2">
              <Link to={`/admin/stages/${s.id}/modifier`} data-testid={`edit-stage-${s.slug}`} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-[11px] font-bold uppercase tracking-wider transition-colors">
                <Pencil size={13} /> Modifier
              </Link>
              <Link to={`/admin/inscriptions?stage=${s.id}`} data-testid={`regs-stage-${s.slug}`} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-[11px] font-bold uppercase tracking-wider transition-colors">
                <Users size={13} /> Voir les inscrits
              </Link>
              {s.status === "open" && (
                <button data-testid={`close-stage-${s.slug}`} onClick={() => setConfirm({ kind: "close", stage: s })} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover text-[11px] font-bold uppercase tracking-wider transition-colors">
                  <Lock size={13} /> Fermer les inscriptions
                </button>
              )}
              {(s.status === "closed" || s.status === "draft") && (
                <button data-testid={`open-stage-${s.slug}`} onClick={() => setConfirm({ kind: "open", stage: s })} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-gold hover:text-gold text-[11px] font-bold uppercase tracking-wider transition-colors">
                  <LockOpen size={13} /> Ouvrir les inscriptions
                </button>
              )}
              <button data-testid={`duplicate-stage-${s.slug}`} onClick={() => duplicate(s)} disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-royal-light hover:text-royal-light text-[11px] font-bold uppercase tracking-wider transition-colors">
                <Copy size={13} /> Dupliquer
              </button>
              {s.status !== "archived" && (
                <button data-testid={`archive-stage-${s.slug}`} onClick={() => setConfirm({ kind: "archive", stage: s })} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-royal-light hover:text-royal-light text-[11px] font-bold uppercase tracking-wider transition-colors">
                  <Archive size={13} /> Archiver
                </button>
              )}
              <button data-testid={`delete-stage-${s.slug}`} onClick={() => setConfirm({ kind: "delete", stage: s })} className="inline-flex items-center gap-2 px-4 py-2 border border-white/15 text-white/60 hover:border-rouge hover:text-rouge-hover text-[11px] font-bold uppercase tracking-wider transition-colors">
                <Trash2 size={13} /> Supprimer
              </button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-white/40 text-sm">Aucun stage trouvé.</p>}
      </div>

      {confirm && (
        <div data-testid="stage-action-confirm" className="fixed inset-0 z-50 bg-noir/90 backdrop-blur flex items-center justify-center p-6">
          <div className="bg-noir-surface border border-rouge/40 p-8 max-w-md w-full">
            <h2 className="font-display uppercase text-2xl tracking-wide text-rouge-hover">{CONFIRM_TEXT[confirm.kind].title}</h2>
            <p className="mt-3 text-white/60 text-sm">
              <strong className="text-white">{confirm.stage.name}</strong> — {CONFIRM_TEXT[confirm.kind].text}
            </p>
            <div className="mt-6 flex gap-3">
              <button data-testid="stage-action-yes" onClick={runAction} disabled={busy} className="px-6 py-3 bg-rouge text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:bg-rouge-hover disabled:opacity-60 flex items-center gap-2">
                {busy && <Loader2 size={14} className="animate-spin" />} {CONFIRM_TEXT[confirm.kind].label}
              </button>
              <button data-testid="stage-action-no" onClick={() => setConfirm(null)} className="px-6 py-3 border border-white/20 text-white text-xs font-bold uppercase tracking-[0.15em] rounded-full hover:border-white transition-colors">
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminStages;
